import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Input, Spin, Tag } from 'antd';
import { EditOutlined, MailOutlined, PaperClipOutlined, SendOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import CBModal from './CBModal';
import axiosInstance from '../variables/api/axiosInstance';
import { HAB, can } from '../variables/constants';
import { partieGarantie, adresseUtilisable } from '../services/partieGarantie';

const GREEN = '#1a6b3c';
const { TextArea } = Input;

/**
 * Le courriel de demande de confirmation de garantie, à relire et à corriger.
 *
 * Tant qu'il n'est pas parti, l'agent le voit tel qu'il partira et peut en changer
 * l'adresse (celle du référentiel est parfois provisoire), l'objet et le message. Une
 * fois parti, il le relit tel qu'il a été envoyé, le retrouve dans la messagerie
 * (Envoyés, Garanties), et peut le renvoyer corrigé.
 */
export default function DemandeGarantieCourrielModal({ open, onClose, sinistre, onEnvoye }) {
  // Fenêtre dans la langue de l'agent ; l'objet et le message viennent du serveur,
  // rédigés dans la langue du destinataire.
  const { t } = useTranslation('documents');
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [apercu, setApercu] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [edition, setEdition] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const email = Form.useWatch('destinataireEmail', { form, preserve: true });
  const id = sinistre?.sinistreTrackingId;
  const partie = partieGarantie(sinistre);
  const peutEnvoyer = can(HAB.SINISTRE_GARANTIE);

  useEffect(() => {
    if (!open || !id) return;
    setApercu(null);
    setChargement(true);
    axiosInstance.get(`/v1/sinistres/${id}/demande-garantie/apercu`)
      .then((r) => {
        const a = r.data?.data || null;
        setApercu(a);
        // Déjà parti : on relit d'abord ; sinon on arrive directement sur le brouillon.
        setEdition(a?.statutEnvoi !== 'ENVOYE' && peutEnvoyer);
      })
      .catch(() => { setApercu(null); onClose(); })
      .finally(() => setChargement(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, id]);

  const envoyer = async () => {
    const v = await form.validateFields();
    setEnvoi(true);
    try {
      const res = await axiosInstance.post(`/v1/sinistres/${id}/demande-garantie/envoyer`, {
        destinataireEmail: v.destinataireEmail.trim(),
        objet: v.objet,
        corps: v.corps,
      });
      const message = res.data?.message || t('ecran.courrielDemande.demandeEnvoyee');
      if (/NON envoyé/i.test(message)) toast.warning(message); else toast.success(message);
      onEnvoye?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('ecran.courrielDemande.nonEnvoyee'));
    } finally {
      setEnvoi(false);
    }
  };

  const vers = `${partie.a} ${apercu?.destinataire || partie.libelle || ''}`.trim();
  const envoye = apercu?.statutEnvoi === 'ENVOYE';
  // L'adresse saisie remplace celle du référentiel : elle doit elle aussi pouvoir arriver.
  const adresseDifferente = apercu && !apercu.motifAdresse && email && email.trim() !== apercu.email;

  const pieces = (apercu?.piecesJointes || []).length > 0 ? (
    <div style={{ marginTop: 4 }}>
      {apercu.piecesJointes.map((p) => (
        <Tag key={p} icon={<PaperClipOutlined />} style={{ marginBottom: 4 }}>{p}</Tag>
      ))}
    </div>
  ) : <span style={{ color: '#8c8c8c' }}>{t('ecran.courrielDemande.aucune')}</span>;

  const pied = edition ? [
    <Button key="annuler" onClick={envoye ? () => setEdition(false) : onClose}>
      {envoye ? t('ecran.courrielDemande.revenir') : t('ecran.annuler')}
    </Button>,
    <Button key="envoyer" type="primary" icon={<SendOutlined />} loading={envoi}
      disabled={!adresseUtilisable(email)} onClick={envoyer}
      style={{ background: GREEN, borderColor: GREEN }}>
      {envoye ? t('ecran.courrielDemande.renvoyer') : t('ecran.courrielDemande.envoyer')}
    </Button>,
  ] : [
    <Button key="fermer" onClick={onClose}>{t('ecran.courrielDemande.fermer')}</Button>,
    envoye && apercu?.courrierTrackingId ? (
      <Button key="messagerie" icon={<MailOutlined />}
        onClick={() => navigate(`/messagerie?dossier=sent&courrier=${apercu.courrierTrackingId}`)}>
        {t('ecran.courrielDemande.ouvrirMessagerie')}
      </Button>
    ) : null,
    peutEnvoyer ? (
      <Button key="modifier" type="primary" icon={<EditOutlined />} onClick={() => setEdition(true)}
        style={{ background: GREEN, borderColor: GREEN }}>
        {t('ecran.courrielDemande.modifierRenvoyer')}
      </Button>
    ) : null,
  ].filter(Boolean);

  return (
    <CBModal
      title={t('ecran.courrielDemande.garantie.titre', { vers })}
      open={open}
      onCancel={onClose}
      footer={pied}
      width={760}
      destroyOnClose
    >
      {chargement || !apercu ? (
        <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>
      ) : (
        <>
          {envoye && !edition && (
            <Alert type="success" showIcon style={{ marginBottom: 16, borderRadius: 10 }}
              message={t('ecran.courrielDemande.garantie.envoye', { vers })}
              description={[
                apercu.dateEnvoi ? t('ecran.courrielDemande.le', { date: dayjs(apercu.dateEnvoi).format(t('ecran.courrielDemande.formatDateHeure')) }) : null,
                apercu.reference ? t('ecran.courrielDemande.ref', { reference: apercu.reference }) : null,
                t('ecran.courrielDemande.garantie.visibleMessagerie'),
              ].filter(Boolean).join(' · ')} />
          )}
          {!apercu.statutEnvoi && !edition && (
            <Alert type="info" showIcon style={{ marginBottom: 16, borderRadius: 10 }}
              message={t('ecran.courrielDemande.pasEncoreEnvoye')} />
          )}
          {apercu.statutEnvoi === 'ECHEC' && (
            <Alert type="warning" showIcon style={{ marginBottom: 16, borderRadius: 10 }}
              message={t('ecran.courrielDemande.dernierEchec')}
              description={apercu.reference
                ? t('ecran.courrielDemande.garantie.echecAvecReference', { reference: apercu.reference })
                : t('ecran.courrielDemande.echec')} />
          )}
          {apercu.motifAdresse && edition && (
            <Alert type="warning" showIcon style={{ marginBottom: 16, borderRadius: 10 }}
              message={apercu.motifAdresse}
              description={t('ecran.courrielDemande.adresseReelle')} />
          )}

          {edition ? (
            <Form form={form} layout="vertical" requiredMark={false}
              initialValues={{
                // Une adresse provisoire n'est pas proposée : elle n'arriverait nulle part.
                destinataireEmail: apercu.motifAdresse ? '' : apercu.email,
                objet: apercu.objet || '',
                corps: apercu.corps || '',
              }}>
              <Form.Item label={t('ecran.courrielDemande.destinataire')} style={{ marginBottom: 8 }}>
                <strong>{vers}</strong>
              </Form.Item>
              <Form.Item name="destinataireEmail" label={t('ecran.courrielDemande.adresseCourriel')}
                extra={adresseDifferente ? t('ecran.courrielDemande.adresseReferentiel', { email: apercu.email }) : null}
                rules={[
                  { required: true, whitespace: true, message: t('ecran.courrielDemande.adresseRequise') },
                  { validator: (_, v) => (!v || adresseUtilisable(v) ? Promise.resolve()
                    : Promise.reject(new Error(t('ecran.courrielDemande.adresseInvalide')))) },
                ]}>
                <Input placeholder="sinistres@compagnie.tg" />
              </Form.Item>
              <Form.Item name="objet" label={t('ecran.courrielDemande.objet')} rules={[{ required: true, whitespace: true, message: t('ecran.courrielDemande.objetRequis') }]}>
                <Input maxLength={250} />
              </Form.Item>
              <Form.Item name="corps" label={t('ecran.courrielDemande.message')} rules={[{ required: true, whitespace: true, message: t('ecran.courrielDemande.messageRequis') }]}>
                <TextArea autoSize={{ minRows: 12, maxRows: 24 }} />
              </Form.Item>
              <Form.Item label={t('ecran.courrielDemande.piecesJointes')} style={{ marginBottom: 0 }}>{pieces}</Form.Item>
            </Form>
          ) : (
            <div>
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>{t('ecran.courrielDemande.objet')}</div>
              <div style={{ fontWeight: 600, marginBottom: 12 }}>{apercu.objet}</div>
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>{t('ecran.courrielDemande.message')}</div>
              <div style={{
                whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: '#fafafa',
                border: '1px solid #f0f0f0', borderRadius: 10, padding: 14, marginBottom: 12,
                maxHeight: 420, overflowY: 'auto',
              }}>
                {apercu.corps}
              </div>
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>{t('ecran.courrielDemande.piecesJointes')}</div>
              {pieces}
            </div>
          )}
        </>
      )}
    </CBModal>
  );
}
